import { getVisibleNodes } from '../resource/hierarchy.js';
import { formatMoneyOrDash } from '../resource/format.js';
import type { CostTableProps, CostTableRow } from './types.js';

/**
 * 成本表：每個任務的固定成本／總成本／基準成本／差異／實際成本／剩餘成本，
 * 支援 WBS 階層與摘要 rollup 呈現。純展示元件。
 *
 * 標紅規則：`variance`（= totalCost − baselineCost）> 0 代表總成本高於基準
 * （超支）。無基準線時 `variance` 為 null，顯示「—」，不視為超支也不視為 0。
 */
export function CostTable(props: CostTableProps): React.JSX.Element {
  const { rows, currency, collapsedTaskIds, onToggleCollapse } = props;

  const visible = getVisibleNodes(
    rows.map((row) => ({ id: row.taskId as string, parentId: row.parentId as string | null, row })),
    collapsedTaskIds as readonly string[],
  );
  const collapsedSet = new Set(collapsedTaskIds as readonly string[]);

  return (
    <div data-testid="cost-table" className="flex h-full w-full flex-col overflow-auto bg-surface text-sm text-fg">
      <div className="flex border-b border-line-strong bg-raised font-medium">
        <div className="w-16 shrink-0 border-r border-line px-2 py-1">WBS</div>
        <div className="w-56 shrink-0 border-r border-line px-2 py-1">任務名稱</div>
        <div className="w-28 shrink-0 border-r border-line px-2 py-1 text-right">固定成本</div>
        <div className="w-28 shrink-0 border-r border-line px-2 py-1 text-right">總成本</div>
        <div className="w-28 shrink-0 border-r border-line px-2 py-1 text-right">基準成本</div>
        <div className="w-28 shrink-0 border-r border-line px-2 py-1 text-right">差異</div>
        <div className="w-28 shrink-0 border-r border-line px-2 py-1 text-right">實際成本</div>
        <div className="w-28 shrink-0 px-2 py-1 text-right">剩餘成本</div>
      </div>

      {visible.map(({ row }: { row: CostTableRow }) => {
        const isOverspend = row.variance !== null && row.variance > 0;
        return (
          <div
            key={row.taskId}
            role="row"
            data-testid={`cost-table-row-${row.taskId}`}
            data-overspend={isOverspend}
            className={[
              'flex border-b border-hairline',
              row.isSummary ? 'font-bold' : '',
              isOverspend ? 'bg-red-100 text-red-900 dark:bg-danger-soft dark:text-danger' : '',
            ].join(' ')}
          >
            <div className="w-16 shrink-0 truncate border-r border-hairline px-2 py-1">{row.wbs}</div>
            <div
              className="flex w-56 shrink-0 items-center gap-1 overflow-hidden border-r border-hairline px-2 py-1"
              style={{ paddingLeft: 8 + (row.outlineLevel - 1) * 16 }}
            >
              {row.isSummary ? (
                <button
                  type="button"
                  aria-label={collapsedSet.has(row.taskId) ? '展開' : '收合'}
                  data-testid={`cost-table-toggle-${row.taskId}`}
                  className="w-3 shrink-0 text-fg-muted"
                  onClick={() => onToggleCollapse(row.taskId)}
                >
                  {collapsedSet.has(row.taskId) ? '▶' : '▼'}
                </button>
              ) : null}
              <span className="truncate">{row.name}</span>
            </div>
            <div className="w-28 shrink-0 border-r border-hairline px-2 py-1 text-right">
              {formatMoneyOrDash(row.fixedCost, currency)}
            </div>
            <div className="w-28 shrink-0 border-r border-hairline px-2 py-1 text-right">
              {formatMoneyOrDash(row.totalCost, currency)}
            </div>
            <div className="w-28 shrink-0 border-r border-hairline px-2 py-1 text-right">
              {formatMoneyOrDash(row.baselineCost, currency)}
            </div>
            <div
              data-testid={`cost-table-variance-${row.taskId}`}
              className="w-28 shrink-0 border-r border-hairline px-2 py-1 text-right"
            >
              {formatMoneyOrDash(row.variance, currency)}
            </div>
            <div className="w-28 shrink-0 border-r border-hairline px-2 py-1 text-right">
              {formatMoneyOrDash(row.actualCost, currency)}
            </div>
            <div className="w-28 shrink-0 px-2 py-1 text-right">{formatMoneyOrDash(row.remainingCost, currency)}</div>
          </div>
        );
      })}
    </div>
  );
}

export default CostTable;
